import { useState } from "react";
import dayjs from "dayjs";
import { ReactComponent as HistoryIcon } from "../../../../assets/icons/HistoryIcon.svg";
import { ReactComponent as DropdownIcon } from "../../../../assets/icons/paraplanningDashboard/ArrowDropdown.svg";

const EMPTY_VALUE = "-";

const formatFieldLabel = (fieldPath = "") =>
  fieldPath
    .replace(/domains\.(\d+)/g, (_, index) => `Domain ${Number(index) + 1}`)
    .split(".")
    .map((part) => part.replace(/([A-Z])/g, " $1").replace(/^./, (char) => char.toUpperCase()))
    .join(" / ");

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return EMPTY_VALUE;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const normaliseEmptyValue = (value) =>
  value === null || value === undefined || value === "" ? "" : value;

const flattenChanges = (before, after, path) => {
  const beforeObject = before && typeof before === "object";
  const afterObject = after && typeof after === "object";

  if (beforeObject || afterObject) {
    const keys = new Set([
      ...Object.keys(beforeObject ? before : {}),
      ...Object.keys(afterObject ? after : {}),
    ]);
    return [...keys].flatMap((key) =>
      flattenChanges(before?.[key], after?.[key], `${path}.${key}`),
    );
  }

  return normaliseEmptyValue(before) === normaliseEmptyValue(after)
    ? []
    : [{ fieldPath: path, before, after }];
};

const getLogChanges = (log) =>
  Object.entries(log.changes || {}).flatMap(([field, values]) =>
    flattenChanges(values?.before, values?.after, field),
  );

const WebsiteAuditLogs = ({ logs = [], loading = false }) => {
  const [open, setOpen] = useState(false);
  const visibleLogs = logs
    .map((log) => ({ ...log, visibleChanges: getLogChanges(log) }))
    .filter((log) => log.visibleChanges.length > 0);

  return <section className="website-audit website-collapsible website-card" aria-labelledby="website-audit-title">
    <div className="website-collapsible__header space-between-row">
      <span id="website-audit-title" className="website-section-heading__label row align-center text-align-left gap-s FP_Content_Semibold">
        <HistoryIcon aria-hidden /> Edit Logs
      </span>
      <button
        type="button"
        className="website-collapse-toggle"
        onClick={() => setOpen((current) => !current)}
        aria-label={open ? "Collapse edit logs" : "Expand edit logs"}
        aria-expanded={open}
      >
        <DropdownIcon className={open ? "website-chevron website-chevron--open" : "website-chevron"} />
      </button>
    </div>

    {open && <div className="website-audit__body">
      {loading && <p className="Content-Small muted">Loading edit logs...</p>}
      {!loading && visibleLogs.length === 0 && (
        <p className="Content-Small muted">No edit logs found yet. New changes will appear here after saving.</p>
      )}

      {!loading && visibleLogs.length > 0 && (
        <div className="fit-column gap-m">
          {visibleLogs.map((log) => {
          const changedAt = log.createdAt?.toDate?.() || log.createdAtMs;
          const changedBy = log.actor?.displayName || log.actor?.email || "Unknown user";
          const changes = log.visibleChanges;

          return (
            <article className="website-audit__item" key={log.id}>
              <div className="website-audit__meta row justify-space-between gap-m">
                <span className="FP_Help_Text_Semibold">{changedBy}</span>
                <span className="FP_Help_Text muted">
                  {changedAt ? dayjs(changedAt).format("DD MMM YYYY, HH:mm") : EMPTY_VALUE}
                </span>
              </div>
              <div className="fit-column gap-m">
                {changes.map((change, index) => (
                  <div className="website-audit__change" key={`${log.id}-${change.fieldPath}-${index}`}>
                    <div className="FP_Help_Text_Semibold">{formatFieldLabel(change.fieldPath)}</div>
                    <div className="FP_Help_Text"><span className="muted">From:</span> {formatValue(change.before)}</div>
                    <div className="FP_Help_Text"><span className="muted">To:</span> {formatValue(change.after)}</div>
                  </div>
                ))}
              </div>
            </article>
          );
          })}
        </div>
      )}
    </div>}
  </section>;
};

export default WebsiteAuditLogs;
